import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Input, message, Modal, Result } from 'antd';
import { MobileOutlined, SafetyOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';

import authService from '../../services/auth';
import tianaiCaptcha from '../../services/captcha';
import { getUserInfoAsync } from '../../redux/slices/authSlice';
import './Register.css';

/**
 * 微信登录绑定手机号页。
 *
 * - 从 URL 读取 bindToken（微信登录流程下发）并在绑定成功后写入登录态
 * - 发送短信验证码时复用后端分级风控：可能触发人机验证，完成验证后再发送
 */
const WeChatBindPhonePage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // token：用于将"微信 openId/session"与手机号绑定；redirect：绑定成功后的跳转目标
  const bindToken = searchParams.get('token') || '';
  const redirect = useMemo(() => searchParams.get('redirect') || '/', [searchParams]);

  const [form] = Form.useForm();
  const [countdown, setCountdown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [smsSendInterval, setSmsSendInterval] = useState(60); // 默认60秒

  // 验证码相关状态
  const [captchaVisible, setCaptchaVisible] = useState(false);
  const [pendingPhone, setPendingPhone] = useState('');
  const [pendingPurpose, setPendingPurpose] = useState('');

  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // 获取短信验证码配置（发送间隔等）
  useEffect(() => {
    authService.getCaptchaConfig()
      .then(res => {
        if (res?.data?.smsSendInterval) {
          setSmsSendInterval(res.data.smsSendInterval);
        }
      })
      .catch(err => {
        console.error('获取验证码配置失败:', err);
        // 使用默认值60秒
      });
  }, []);

  const handleSendCode = async () => {
    try {
      const phone = form.getFieldValue('phone');
      if (!phone) {
        message.warning('请先输入手机号');
        return;
      }
      const res = await authService.sendVerificationCode(phone, 'bind-phone');
      const data = res?.data;
      if (data?.captchaRequired && !data?.sent) {
        // 需要完成人机验证，打开验证码弹窗
        setPendingPhone(phone);
        setPendingPurpose('bind-phone');
        setCaptchaVisible(true);
        message.warning(res?.message || '需要完成人机验证');
        return;
      }
      message.success('验证码已发送');
      setCountdown(smsSendInterval);
    } catch (e) {
      message.error(e?.message || '发送验证码失败');
    }
  };

  const handleBind = async (values) => {
    if (!bindToken) return;
    setSubmitting(true);
    try {
      const response = await authService.wechatBindPhone({
        bindToken,
        phone: values.phone,
        verificationCode: values.verificationCode,
        rememberMe: values.rememberMe,
      });
      const data = response.data;

      if (data?.user) {
        await dispatch(getUserInfoAsync());
        navigate(redirect || '/', { replace: true });
        return;
      }

      message.error('绑定失败，请重试');
    } catch (e) {
      message.error(e?.message || '绑定失败，请重试');
    } finally {
      setSubmitting(false);
    }
  };

  // 处理验证码验证成功
  const handleCaptchaSuccess = async (token) => {
    try {
      // 使用验证码token发送短信验证码
      const res = await authService.sendVerificationCode(pendingPhone, pendingPurpose, token);
      const responseData = res?.data;

      if (responseData?.sent) {
        message.success('验证码已发送');
        setCountdown(smsSendInterval);
        setCaptchaVisible(false);
      } else {
        message.error(responseData?.message || '发送验证码失败');
      }
    } catch (error) {
      message.error(error?.message || '发送验证码失败');
    }
  };

  // 当验证码弹窗打开时，初始化验证码
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!captchaVisible) return;

    let disposed = false;
    const timer = setTimeout(() => {
      if (disposed) return;
      tianaiCaptcha.show({
        bindEl: '#tianai-captcha-box',
        onValidSuccess: (token, tac) => {
          handleCaptchaSuccess(token);
          tac.destroyWindow();
          setCaptchaVisible(false);
        },
        onValidFail: () => {
          message.error('验证失败，请重试');
        }
      }).catch((error) => {
        // console.error('验证码初始化失败:', error);
        message.error(error?.message || '验证码初始化失败');
        setCaptchaVisible(false);
      });
    }, 0);

    return () => {
      disposed = true;
      clearTimeout(timer);
      tianaiCaptcha.destroy();
    };
  }, [captchaVisible]);

  if (!bindToken) {
    return (
      <Result
        status="error"
        title="绑定信息缺失"
        subTitle="请从微信登录流程进入该页面"
        extra={[
          <Button type="primary" key="back" onClick={() => navigate('/auth/login', { replace: true })}>
            返回登录
          </Button>,
        ]}
      />
    );
  }

  return (
    <div className="register-container">
      <h2 className="section-title">绑定手机号</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleBind}
        size="large"
        initialValues={{ rememberMe: true }}
      >
        <Form.Item
          name="phone"
          rules={[
            { required: true, message: '请输入手机号' },
            { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号' },
          ]}
        >
          <Input prefix={<MobileOutlined />} placeholder="手机号" autoComplete="tel" />
        </Form.Item>

        <Form.Item
          name="verificationCode"
          rules={[
            { required: true, message: '请输入验证码' },
            { len: 6, message: '验证码为6位数字' },
          ]}
        >
          <div className="verification-input">
            <Input prefix={<SafetyOutlined />} placeholder="验证码" autoComplete="one-time-code" />
            <Button onClick={handleSendCode} disabled={countdown > 0} className="send-code-button">
              {countdown > 0 ? `${countdown}s` : '发送验证码'}
            </Button>
          </div>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} block>
            绑定并登录
          </Button>
        </Form.Item>
      </Form>

      {/* 验证码弹窗 */}
      <Modal
        open={captchaVisible}
        title="安全验证"
        onCancel={() => setCaptchaVisible(false)}
        footer={null}
        destroyOnClose
        centered
      >
        <div id="tianai-captcha-box" style={{ minWidth: 318, minHeight: 318 }} />
      </Modal>
    </div>
  );
};

export default WeChatBindPhonePage;
